import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Loader2 } from "lucide-react";
import { useRun } from "@/context/RunContext";
import { fetchCorrectiveActions, updateCorrectiveAction } from "@/lib/api";

export default function CorrectiveActions() {
  const { periodId, period } = useRun();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("OPEN");
  const [selectedTask, setSelectedTask] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchCorrectiveActions({ period_id: periodId, status: filter })
      .then((data) => setTasks(data.tasks || []))
      .catch(() => setTasks([]))
      .finally(() => setLoading(false));
  }, [periodId, filter]);

  const handleUpdate = async (taskId, updates) => {
    try {
      await updateCorrectiveAction(taskId, updates);
      const data = await fetchCorrectiveActions({ period_id: periodId, status: filter });
      setTasks(data.tasks || []);
      setSelectedTask(null);
    } catch (err) {
      console.error("Failed to update task:", err);
    }
  };

  const statusColor = (status) => {
    switch (status) {
      case "OPEN": return "bg-amber-50 text-amber-700 border-amber-200";
      case "IN_PROGRESS": return "bg-blue-50 text-blue-700 border-blue-200";
      case "COMPLETED": return "bg-emerald-50 text-emerald-700 border-emerald-200";
      default: return "bg-zinc-50 text-zinc-700 border-zinc-200";
    }
  };

  return (
    <div className="max-w-5xl" data-testid="corrective-actions-page">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900">
        <ArrowLeft className="h-4 w-4" /> Back to close run
      </Link>

      <div className="mt-8 flex flex-wrap items-baseline justify-between gap-4 border-b border-zinc-200 pb-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-zinc-400">Tasks · {period?.close_type}</p>
          <h1 className="mt-3 font-heading text-3xl font-medium tracking-tight text-zinc-900 sm:text-4xl">Corrective Actions</h1>
          <p className="mt-2 text-sm text-zinc-500">{period?.range} · tasks requiring execution</p>
        </div>
        <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 font-mono text-xs text-blue-800">
          {tasks.length} task{tasks.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="flex gap-4 border-b border-zinc-200 py-4">
        {["OPEN", "IN_PROGRESS", "COMPLETED"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-md font-mono text-xs ${filter === s ? "bg-zinc-900 text-white" : "text-zinc-600 hover:text-zinc-900"}`}>
            {s.replace("_", " ")}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="py-10 flex items-center gap-2 text-sm text-zinc-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading tasks...
        </p>
      ) : tasks.length === 0 ? (
        <p className="py-10 text-sm text-zinc-500">No {filter.replace("_", " ").toLowerCase()} corrective actions.</p>
      ) : (
        <div className="mt-4 space-y-4">
          {tasks.map((task) => (
            <div key={task.task_id} className="rounded-md border border-zinc-200 bg-white p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-mono text-sm font-medium text-zinc-900">{task.invoice_id}</p>
                    <span className={`rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase ${statusColor(task.status)}`}>
                      {task.status.replace("_", " ")}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-zinc-600">{task.description}</p>
                  <dl className="mt-3 grid grid-cols-3 gap-4 text-xs">
                    <div>
                      <dt className="text-zinc-400">Action Type</dt>
                      <dd className="mt-0.5 font-mono text-zinc-900">{task.action_type.replace("_", " ")}</dd>
                    </div>
                    <div>
                      <dt className="text-zinc-400">Assigned Team</dt>
                      <dd className="mt-0.5 font-mono text-zinc-900">{task.assigned_team}</dd>
                    </div>
                    <div>
                      <dt className="text-zinc-400">Priority</dt>
                      <dd className="mt-0.5 font-mono text-zinc-900">{task.priority}</dd>
                    </div>
                  </dl>
                </div>
                <div className="flex gap-2">
                  {task.status === "OPEN" && (
                    <button onClick={() => handleUpdate(task.task_id, { status: "IN_PROGRESS" })} className="rounded-md border border-zinc-300 px-3 py-1.5 font-mono text-xs text-zinc-600 hover:bg-zinc-50">
                      Start
                    </button>
                  )}
                  {task.status === "IN_PROGRESS" && (
                    <button onClick={() => setSelectedTask(task)} className="rounded-md bg-emerald-600 px-3 py-1.5 font-mono text-xs text-white hover:bg-emerald-700">
                      Complete
                    </button>
                  )}
                </div>
              </div>
              {task.assigned_to && <p className="mt-3 text-xs text-zinc-500">Assigned to: {task.assigned_to}</p>}
            </div>
          ))}
        </div>
      )}

      {selectedTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
          <div className="rounded-lg bg-white p-6 shadow-xl max-w-md w-full">
            <h3 className="font-heading text-lg font-medium">Complete Task</h3>
            <p className="mt-2 text-sm text-zinc-600">Mark this corrective action as completed.</p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-xs text-zinc-500">Assignee</label>
                <input type="text" placeholder="Who completed this..." className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm font-mono" onChange={(e) => selectedTask.assigned_to = e.target.value} />
              </div>
              <div>
                <label className="text-xs text-zinc-500">Notes</label>
                <textarea placeholder="Completion notes..." className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm font-mono" rows={3} onChange={(e) => selectedTask.completion_notes = e.target.value} />
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setSelectedTask(null)} className="flex-1 rounded-md border border-zinc-300 px-4 py-2 text-sm text-zinc-600 hover:bg-zinc-50">Cancel</button>
              <button onClick={() => handleUpdate(selectedTask.task_id, { status: "COMPLETED", assigned_to: selectedTask.assigned_to, completion_notes: selectedTask.completion_notes })} className="flex-1 rounded-md bg-emerald-600 px-4 py-2 text-sm text-white hover:bg-emerald-700">Complete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
